import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { formatFecha } from '../utils/fecha';

import { API } from '../config/api';

const EMPTY = {
  cobro_id: '', fecha_pago: '', monto: '',
  moneda: 'Colones',
  medio_pago: 'Transferencia',
  medio_pago_id: '',
  numero_referencia: '', banco_destino: '',
  comprobante_url: '', notas: ''
};

const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%',
  boxSizing: 'border-box', fontFamily: 'Segoe UI, sans-serif'
};

const formatMonto  = (m) => m != null ? Number(m).toLocaleString('es-CR', { minimumFractionDigits: 2 }) : '0,00';
const simMon       = (m) => m === 'Dólares' ? '$' : '₡';

// v27: display preferente con medio_pago_nombre (JOIN catálogo) y fallback a texto legacy
const medioDisplay = (p) => p?.medio_pago_nombre || p?.medio_pago || '—';

const medioBadge = (p) => <span style={{ background: '#f0f0f0', color: '#555', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>{medioDisplay(p)}</span>;
const bancoBadge = (b) => <span style={{ background: '#eef5ff', color: '#2B5E9F', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 500 }}>{b || '—'}</span>;
const monedaBadge = (m) => {
  const s = m === 'Colones'
    ? { background: '#e8f4e4', color: '#27500A' }
    : { background: '#e6f1fb', color: '#185FA5' };
  return <span style={{ ...s, padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '500' }}>{m}</span>;
};

// ── Modal eliminar ─────────────────────────────────────────────────────────────

const ModalEliminar = ({ titulo, descripcion, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4v6M10 13v1" stroke="#A32D2D" strokeWidth="1.8" strokeLinecap="round"/>
            <circle cx="10" cy="10" r="8.5" stroke="#A32D2D" strokeWidth="1.4"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#012E40' }}>{titulo}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{descripcion}</div>
        </div>
      </div>
      <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', margin: '0 0 20px' }}>
        ¿Estás seguro que querés eliminar este registro de forma permanente? Esta acción <strong>no se puede deshacer</strong> y recalculará el estado del cobro.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button onClick={onCancel} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
        <button onClick={onConfirm} style={{ padding: '8px 20px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Sí, eliminar</button>
      </div>
    </div>
  </div>
);

// ── Modal de pago recibido — exportado para uso desde CobrosCliente ────────────

export function ModalPagoCliente({ cobroInicial = null, cobrosDisponibles = [], pagoEditar = null, onGuardado, onCerrar }) {
  const isAdmin = useAdmin();

  const [form,              setForm]              = useState(EMPTY);
  const [cobroSeleccionado, setCobroSeleccionado] = useState(null);
  const [saldoCobro,        setSaldoCobro]        = useState(null);
  const [montoTocado,       setMontoTocado]       = useState(false);
  const [guardando,         setGuardando]         = useState(false);
  // v27: medios ahora es array de objetos {id, valor}; bancos sigue siendo string[]
  const [listas,            setListas]            = useState({ medios: [], bancos: [] });

  // Cargar listas configurables
  useEffect(() => {
    (async () => {
      try {
        const [m, b] = await Promise.all([
          axios.get(`${API}/listas?lista=medio_pago_cxc`),
          axios.get(`${API}/listas?lista=banco_destino`)
        ]);
        setListas({
          medios: (m.data || []).map(x => ({ id: x.id, valor: x.valor })),
          bancos: (b.data || []).map(x => x.valor)
        });
      } catch (e) {
        // Fallback hardcoded si listas no están cargadas (id=null → backend resuelve por texto)
        setListas({
          medios: [
            { id: null, valor: 'Transferencia' },
            { id: null, valor: 'SINPE Móvil' },
            { id: null, valor: 'Cheque' },
            { id: null, valor: 'Efectivo' }
          ],
          bancos: ['BAC', 'Banco Nacional', 'Banco de Costa Rica', 'Banco Popular']
        });
      }
    })();
  }, []);

  // v27: al cargar listas, si el form tiene texto pero no id, resuelvo el id
  useEffect(() => {
    if (!listas.medios.length) return;
    if (form.medio_pago && !form.medio_pago_id) {
      const sel = listas.medios.find(x => x.valor === form.medio_pago);
      if (sel && sel.id != null) {
        setForm(prev => ({ ...prev, medio_pago_id: String(sel.id) }));
      }
    }
  }, [listas, form.medio_pago, form.medio_pago_id]);

  // Cargar saldo del cobro
  const cargarSaldo = useCallback(async (cid, excludePagoId = null) => {
    if (!cid) { setSaldoCobro(null); return null; }
    try {
      const url = excludePagoId
        ? `${API}/pagos-cliente?action=saldo&cobro_id=${cid}&exclude_pago_id=${excludePagoId}`
        : `${API}/pagos-cliente?action=saldo&cobro_id=${cid}`;
      const res = await axios.get(url);
      setSaldoCobro(res.data.saldo_cobro || null);
      return res.data.saldo_cobro || null;
    } catch {
      setSaldoCobro(null);
      return null;
    }
  }, []);

  // Inicializar según modo: edición o pre-seleccionado
  useEffect(() => {
    if (pagoEditar) {
      setForm({
        cobro_id:          String(pagoEditar.cobro_id),
        fecha_pago:        pagoEditar.fecha_pago ? pagoEditar.fecha_pago.substring(0, 10) : '',
        monto:             String(pagoEditar.monto || ''),
        moneda:            pagoEditar.moneda || 'Colones',
        medio_pago:        pagoEditar.medio_pago_nombre || pagoEditar.medio_pago || 'Transferencia',
        medio_pago_id:     pagoEditar.medio_pago_id != null ? String(pagoEditar.medio_pago_id) : '',
        numero_referencia: pagoEditar.numero_referencia || '',
        banco_destino:     pagoEditar.banco_destino || '',
        comprobante_url:   pagoEditar.comprobante_url || '',
        notas:             pagoEditar.notas || ''
      });
      setMontoTocado(true);
      cargarSaldo(pagoEditar.cobro_id, pagoEditar.id);
      return;
    }
    if (cobroInicial) {
      setForm({ ...EMPTY, cobro_id: String(cobroInicial.id), moneda: cobroInicial.moneda || 'Colones' });
      setCobroSeleccionado(cobroInicial);
      setMontoTocado(false);
      cargarSaldo(cobroInicial.id).then(sc => {
        if (sc && !sc.esta_cobrada) {
          setForm(prev => ({ ...prev, monto: String(sc.saldo_disponible) }));
        }
      });
    }
  }, [cobroInicial, pagoEditar, cargarSaldo]);

  const seleccionarCobro = async (cid) => {
    const c = cobrosDisponibles.find(x => String(x.id) === String(cid));
    setCobroSeleccionado(c || null);
    setMontoTocado(false);
    setForm(prev => ({
      ...prev,
      cobro_id: cid,
      moneda: c ? c.moneda : 'Colones',
      monto: ''
    }));
    const sc = await cargarSaldo(cid);
    if (sc && !sc.esta_cobrada) {
      setForm(prev => ({ ...prev, monto: String(sc.saldo_disponible) }));
    }
  };

  const cambiarMonto = (v) => {
    setMontoTocado(true);
    setForm(prev => ({ ...prev, monto: v }));
  };

  // v27: combo medio_pago — guarda texto + resuelve id desde el catálogo
  const cambiarMedio = (txt) => {
    const sel = listas.medios.find(m => m.valor === txt);
    setForm(prev => ({
      ...prev,
      medio_pago: txt,
      medio_pago_id: sel && sel.id != null ? String(sel.id) : ''
    }));
  };

  const montoNum = parseFloat(form.monto) || 0;
  const saldoDisp = saldoCobro ? saldoCobro.saldo_disponible : 0;
  const excedente = montoNum > 0 && saldoCobro ? +(montoNum - saldoDisp).toFixed(2) : 0;
  const excede    = saldoCobro != null && excedente > 0.01;

  const guardar = async () => {
    if (!form.cobro_id || !form.fecha_pago || !form.monto) {
      alert('Completá los campos obligatorios: Factura, Fecha y Monto.');
      return;
    }
    if (montoNum <= 0) {
      alert('El monto debe ser mayor que cero.');
      return;
    }
    if (!form.medio_pago) {
      alert('El medio de pago es obligatorio.');
      return;
    }
    if (!form.banco_destino && form.medio_pago !== 'Efectivo') {
      alert('El banco destino es obligatorio (excepto en pagos en efectivo).');
      return;
    }

    // Excede: bloqueo duro si no-admin, doble confirm si admin
    if (excede && !isAdmin) {
      alert(`El monto (${montoNum.toFixed(2)}) excede el saldo disponible (${saldoDisp.toFixed(2)}).\nSolo un administrador puede forzar este pago.`);
      return;
    }

    let forzar = false;
    if (excede && isAdmin) {
      const ok1 = window.confirm(
        `⚠ El pago excede el saldo disponible por ${excedente.toFixed(2)} ${form.moneda}.\n\nSaldo: ${saldoDisp.toFixed(2)}\nMonto a pagar: ${montoNum.toFixed(2)}\n\n¿Forzar el registro?`
      );
      if (!ok1) return;
      const ok2 = window.confirm('CONFIRMACIÓN FINAL\n\nAl forzar este pago se excederá el monto total del cobro.\n¿Continuar?');
      if (!ok2) return;
      forzar = true;
    }

    setGuardando(true);
    try {
      const urlBase = pagoEditar
        ? `${API}/pagos-cliente?id=${pagoEditar.id}`
        : `${API}/pagos-cliente`;
      const url = forzar ? `${urlBase}${urlBase.includes('?') ? '&' : '?'}force=true` : urlBase;
      const res = pagoEditar
        ? await axios.put(url, form)
        : await axios.post(url, form);
      if (res?.data?.advertencia) alert('Pago registrado con advertencia: ' + res.data.advertencia);
      onGuardado && onGuardado(res.data);
    } catch (e) {
      // v27: distinguir 409 por estado del cobro vs 409 por saldo excedido
      if (e.response?.status === 409) {
        const errData = e.response.data || {};
        if (errData.cobro_estado) {
          alert(`No se puede registrar el pago.\n\n${errData.error}`);
        } else {
          alert(`No se puede registrar: excede el saldo.\n\n${errData.error || ''}`);
        }
      } else if (e.response?.status === 400) {
        alert(e.response.data?.error || 'Datos inválidos.');
      } else {
        alert('Error al registrar pago: ' + (e.response?.data?.error || e.message));
      }
    } finally {
      setGuardando(false);
    }
  };

  const esEfectivo = form.medio_pago === 'Efectivo';

  // Estilo dinámico panel saldo
  const panelSaldoStyle = (() => {
    if (!saldoCobro) return null;
    if (saldoCobro.esta_cobrada) {
      return { bg: '#FCEBEB', border: '#F7C1C1', color: '#A32D2D', label: 'Cobro ya saldado' };
    }
    if (excede) {
      return { bg: '#FCEBEB', border: '#F7C1C1', color: '#A32D2D', label: 'Excede saldo' };
    }
    return { bg: '#e8f4e4', border: '#C0DD97', color: '#2d6a2d', label: 'Saldo disponible' };
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '560px', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#012E40', fontSize: '16px', fontWeight: '700', margin: 0 }}>
            {pagoEditar ? 'Editar pago recibido' : 'Registrar pago recibido'}
          </h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
        </div>

        {/* Selector de cobro — oculto si viene pre-cargado o editando */}
        {!cobroInicial && !pagoEditar && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Factura *</label>
            <select value={form.cobro_id || ''} onChange={e => seleccionarCobro(e.target.value)} style={inputStyle}>
              <option value=''>Seleccionar factura pendiente...</option>
              {cobrosDisponibles.map(c => (
                <option key={c.id} value={c.id}>
                  {c.numero_factura_cliente || '(sin #)'} — {c.cliente_nombre || c.numero_proyecto} ({c.moneda})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Info cobro + desglose IVA */}
        {(cobroSeleccionado || saldoCobro) && (
          <div style={{ background: '#f0f7e8', border: '1px solid #C0DD97', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px' }}>
            <div style={{ fontWeight: '600', color: '#012E40', marginBottom: '6px' }}>
              {saldoCobro?.numero_factura_cliente || cobroSeleccionado?.numero_factura_cliente || '(sin número)'} — {saldoCobro?.numero_proyecto || cobroSeleccionado?.numero_proyecto || ''}
            </div>
            {saldoCobro && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', color: '#555' }}>
                <div>Subtotal (sin IVA):</div>
                <div style={{ textAlign: 'right' }}>{simMon(saldoCobro.moneda)}{formatMonto(saldoCobro.monto_sin_iva)}</div>
                <div>IVA:</div>
                <div style={{ textAlign: 'right' }}>{simMon(saldoCobro.moneda)}{formatMonto(saldoCobro.monto_iva)}</div>
                <div style={{ fontWeight: 700, color: '#012E40', borderTop: '1px solid #C0DD97', paddingTop: 4, marginTop: 2 }}>Total factura:</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: '#012E40', borderTop: '1px solid #C0DD97', paddingTop: 4, marginTop: 2 }}>
                  {simMon(saldoCobro.moneda)}{formatMonto(saldoCobro.monto_total)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Panel saldo dinámico */}
        {saldoCobro && panelSaldoStyle && (
          <div style={{
            background: panelSaldoStyle.bg, border: `1px solid ${panelSaldoStyle.border}`,
            borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: panelSaldoStyle.color, fontWeight: '600' }}>{panelSaldoStyle.label}</span>
              <span style={{ color: panelSaldoStyle.color, fontWeight: '700', fontSize: '14px' }}>
                {simMon(saldoCobro.moneda)}{formatMonto(saldoCobro.saldo_disponible)}
              </span>
            </div>
            <div style={{ color: '#666', marginTop: '4px', fontSize: '11px' }}>
              Ya pagado: {simMon(saldoCobro.moneda)}{formatMonto(saldoCobro.pagado)} de {simMon(saldoCobro.moneda)}{formatMonto(saldoCobro.monto_total)}
            </div>
          </div>
        )}

        {/* Aviso excedente */}
        {excede && (
          <div style={{ background: '#FCEBEB', border: '1px solid #F7C1C1', borderRadius: '8px', padding: '8px 14px', marginBottom: '10px', fontSize: '12px', color: '#A32D2D' }}>
            ⚠ El monto excede el saldo por <strong>{formatMonto(excedente)} {form.moneda}</strong>.
            {isAdmin ? ' Podés forzar el pago con doble confirmación.' : ' Solo un administrador puede forzar este pago.'}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

          {/* Fecha */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Fecha de pago *</label>
            <input type='date' value={form.fecha_pago || ''} onChange={e => setForm({ ...form, fecha_pago: e.target.value })} style={inputStyle} />
          </div>

          {/* Monto — precargado con saldo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
              Monto recibido * {saldoCobro && !montoTocado && !pagoEditar && (
                <span style={{ color: '#87B749', fontWeight: '400' }}>(precargado con saldo)</span>
              )}
            </label>
            <input type='number' step='0.01' value={form.monto || ''} onChange={e => cambiarMonto(e.target.value)} style={inputStyle} />
          </div>

          {/* Moneda — bloqueada */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
              Moneda * <span style={{ color: '#aaa', fontWeight: '400' }}>(según factura)</span>
            </label>
            <input type='text' value={form.moneda || ''} disabled style={{ ...inputStyle, background: '#f5f6f8', color: '#555', cursor: 'not-allowed' }} />
          </div>

          {/* Medio de pago — v27: combo guarda texto + resuelve id */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Medio de pago *</label>
            <select value={form.medio_pago || ''} onChange={e => cambiarMedio(e.target.value)} style={inputStyle}>
              <option value=''>Seleccionar...</option>
              {listas.medios.map(m => <option key={m.valor} value={m.valor}>{m.valor}</option>)}
            </select>
          </div>

          {/* Banco destino */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
              Banco destino {!esEfectivo && '*'}
              {esEfectivo && <span style={{ color: '#aaa', fontWeight: '400' }}> (no aplica)</span>}
            </label>
            <select value={form.banco_destino || ''} onChange={e => setForm({ ...form, banco_destino: e.target.value })} style={{ ...inputStyle, background: esEfectivo ? '#f5f6f8' : '#fff' }} disabled={esEfectivo}>
              <option value=''>Seleccionar...</option>
              {listas.bancos.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>

          {/* Número de referencia */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>N° Referencia</label>
            <input type='text' value={form.numero_referencia || ''} onChange={e => setForm({ ...form, numero_referencia: e.target.value })} style={inputStyle} placeholder='# Transferencia / Cheque / SINPE' />
          </div>

          {/* Comprobante URL */}
          <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
              URL Comprobante (opcional)
              <span style={{ color: '#aaa', fontWeight: '400' }}> — PDF/JPG/PNG máx 10MB</span>
            </label>
            <input type='text' value={form.comprobante_url || ''} onChange={e => setForm({ ...form, comprobante_url: e.target.value })} style={inputStyle} placeholder='https://...' />
          </div>

          {/* Notas */}
          <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Notas (opcional)</label>
            <input type='text' value={form.notas || ''} onChange={e => setForm({ ...form, notas: e.target.value })} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={onCerrar}
            style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
            Cancelar
          </button>
          {excede && isAdmin ? (
            <button onClick={guardar} disabled={guardando}
              style={{ padding: '8px 20px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : '⚠ Forzar pago'}
            </button>
          ) : (
            <button onClick={guardar} disabled={guardando || (excede && !isAdmin)}
              style={{
                padding: '8px 20px',
                background: (excede && !isAdmin) ? '#ccc' : '#87B749',
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
                cursor: (excede && !isAdmin) ? 'not-allowed' : 'pointer',
                opacity: guardando ? 0.7 : 1
              }}>
              {guardando ? 'Guardando...' : (pagoEditar ? 'Guardar cambios' : 'Registrar pago')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────

export default function PagosCliente() {
  const isAdmin = useAdmin();

  const [lista,          setLista]          = useState([]);
  const [cobros,         setCobros]         = useState([]);
  const [clientes,       setClientes]       = useState([]);
  const [filtro,         setFiltro]         = useState('');
  const [filtroCliente,  setFiltroCliente]  = useState('');
  const [filtroMoneda,   setFiltroMoneda]   = useState('');
  const [filtroBanco,    setFiltroBanco]    = useState('');
  const [modalPago,      setModalPago]      = useState(false);
  const [pagoEditar,     setPagoEditar]     = useState(null);
  const [detalle,        setDetalle]        = useState(null);
  const [cargando,       setCargando]       = useState(true);
  const [modalEliminar,  setModalEliminar]  = useState(null);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setCargando(true);
    try {
      const [pa, co, cl] = await Promise.all([
        axios.get(`${API}/pagos-cliente`),
        axios.get(`${API}/cobros-cliente`),
        axios.get(`${API}/clientes`)
      ]);
      setLista(pa.data || []);
      // v27: alineado con backend C-PAG-CxC-2 — solo cobros que aceptan pago
      // (Pendiente, Parcial, Facturada). Excluye Cobrada, Anulado y legacy 'Pagado'.
      const ESTADOS_EXCLUIDOS = ['Cobrada', 'Pagado', 'Anulado'];
      const cobrosConSaldo = (co.data || [])
        .filter(c =>
          !ESTADOS_EXCLUIDOS.includes(c.estado) &&
          c.numero_factura_cliente &&
          parseFloat(c.saldo ?? c.monto_total ?? 0) > 0.01
        )
        .map(c => {
          const cliente = (cl.data || []).find(x => x.id === c.cliente_id);
          return { ...c, cliente_nombre: cliente?.nombre_comercial || cliente?.razon_social || '' };
        })
        .sort((a, b) => {
          // Prioriza los más próximos a vencer
          const da = a.fecha_vencimiento ? new Date(a.fecha_vencimiento).getTime() : Infinity;
          const db = b.fecha_vencimiento ? new Date(b.fecha_vencimiento).getTime() : Infinity;
          return da - db;
        });
      console.log('[PagosCliente] cobros devueltos por API:', (co.data || []).length,
                  '/ tras filtro:', cobrosConSaldo.length);
      setCobros(cobrosConSaldo);
      setClientes(cl.data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const bancosUnicos = [...new Set((lista || []).map(p => p.banco_destino).filter(Boolean))];
  const clientesUnicos = [...new Set((lista || []).map(p => p.cliente_nombre).filter(Boolean))];

  const filtrados = lista.filter(p => {
    if (filtro && ![p.numero_factura_cliente, p.numero_proyecto, p.cliente_nombre, p.numero_referencia]
        .some(v => (v || '').toLowerCase().includes(filtro.toLowerCase()))) return false;
    if (filtroCliente && p.cliente_nombre !== filtroCliente) return false;
    if (filtroMoneda  && p.moneda         !== filtroMoneda)  return false;
    if (filtroBanco   && p.banco_destino  !== filtroBanco)   return false;
    return true;
  });

  const totalColones = filtrados.filter(p => p.moneda === 'Colones').reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const totalDolares = filtrados.filter(p => p.moneda === 'Dólares').reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const esteMes      = filtrados.filter(p => {
    const d = new Date(p.fecha_pago);
    const n = new Date();
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  }).length;

  const confirmarEliminar = async () => {
    if (!modalEliminar) return;
    try {
      await axios.delete(`${API}/pagos-cliente?id=${modalEliminar.id}&hard=true`);
      setModalEliminar(null);
      setDetalle(null);
      await cargar();
    } catch (e) { alert('Error al eliminar: ' + (e.response?.data?.error || e.message)); }
  };

  const btnFiltroStyle = { padding: '7px 12px', fontSize: '12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', outline: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#012E40', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', margin: 0 }}>Pagos Recibidos</h1>
          <span style={{ background: '#87B749', color: '#fff', fontSize: '11px', padding: '2px 10px', borderRadius: '10px', fontWeight: '500' }}>CxC</span>
        </div>
        <button onClick={() => { setPagoEditar(null); setModalPago(true); }}
          style={{ padding: '8px 18px', background: 'transparent', color: '#87B749', border: '1.5px solid #87B749', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          + Registrar pago
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', padding: '16px 24px', background: '#f5f6f8', flexShrink: 0 }}>
        {[
          { val: filtrados.length,                  lbl: 'Pagos (filtrados)',    color: '#012E40', grande: true  },
          { val: `₡${formatMonto(totalColones)}`,   lbl: 'Cobrado en Colones',   color: '#2d6a2d', grande: false },
          { val: `$${formatMonto(totalDolares)}`,   lbl: 'Cobrado en Dólares',   color: '#185FA5', grande: false },
          { val: esteMes,                           lbl: 'Pagos este mes',       color: '#f57f17', grande: true  },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #eee' }}>
            <div style={{ fontSize: s.grande ? '28px' : '18px', fontWeight: '700', color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ padding: '0 24px 12px', background: '#f5f6f8', flexShrink: 0, display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={filtro} onChange={e => setFiltro(e.target.value)}
          placeholder='Buscar: factura, proyecto, cliente, referencia...'
          style={{ ...inputStyle, padding: '10px 16px', fontSize: '14px', borderRadius: '10px', flex: 1, minWidth: 260 }} />

        <select value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)} style={btnFiltroStyle}>
          <option value=''>Todos los clientes</option>
          {clientesUnicos.map(c => <option key={c}>{c}</option>)}
        </select>

        <select value={filtroMoneda} onChange={e => setFiltroMoneda(e.target.value)} style={btnFiltroStyle}>
          <option value=''>Todas las monedas</option>
          <option value='Colones'>Colones</option>
          <option value='Dólares'>Dólares</option>
        </select>

        <select value={filtroBanco} onChange={e => setFiltroBanco(e.target.value)} style={btnFiltroStyle}>
          <option value=''>Todos los bancos</option>
          {bancosUnicos.map(b => <option key={b}>{b}</option>)}
        </select>
      </div>

      {/* Tabla */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px', background: '#f5f6f8' }}>
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#012E40' }}>
                {['Fecha', 'N° Factura', 'Cliente', 'Proyecto', 'Monto', 'Mon.', 'Medio', 'Banco', 'Referencia', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', borderBottom: '2px solid #87B749' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cargando
                ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Cargando...</td></tr>
                : filtrados.length === 0
                  ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                      {lista.length === 0 ? 'No hay pagos registrados aún' : 'Sin resultados con los filtros actuales'}
                    </td></tr>
                  : filtrados.map((p) => (
                      <tr key={p.id}
                        style={{ borderBottom: '1px solid #f3f3f3', cursor: 'pointer' }}
                        onClick={() => setDetalle(p)}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{formatFecha(p.fecha_pago)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: '#012E40' }}>{p.numero_factura_cliente || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.cliente_nombre || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{p.numero_proyecto || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: '600', color: '#012E40' }}>{simMon(p.moneda)}{formatMonto(p.monto)}</td>
                        <td style={{ padding: '10px 14px' }}>{monedaBadge(p.moneda)}</td>
                        <td style={{ padding: '10px 14px' }}>{medioBadge(p)}</td>
                        <td style={{ padding: '10px 14px' }}>{bancoBadge(p.banco_destino)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{p.numero_referencia || '—'}</td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {p.comprobante_url && (
                              <a href={p.comprobante_url} target='_blank' rel='noreferrer' onClick={e => e.stopPropagation()}
                                style={{ padding: '4px 10px', background: '#eef5ff', border: '1px solid #c6d9f2', borderRadius: 6, fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>
                                📎
                              </a>
                            )}
                            {isAdmin && (
                              <button onClick={e => { e.stopPropagation(); setModalEliminar(p); }}
                                style={{ padding: '4px 12px', background: 'none', border: '1px solid #fcc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Panel detalle */}
      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '520px', maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: '#012E40', fontSize: '18px', fontWeight: '700', margin: '0 0 6px' }}>
                  Pago — {detalle.numero_factura_cliente || '(sin #)'}
                </h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {monedaBadge(detalle.moneda)}
                  {medioBadge(detalle)}
                  {bancoBadge(detalle.banco_destino)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { setPagoEditar(detalle); setDetalle(null); setModalPago(true); }}
                  style={{ padding: '7px 14px', background: '#fff', border: '1px solid #87B749', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#3B6D11', fontWeight: 600 }}>Editar</button>
                {isAdmin && (
                  <button onClick={() => setModalEliminar(detalle)}
                    style={{ padding: '7px 14px', background: 'none', border: '1px solid #fcc', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                )}
                <button onClick={() => setDetalle(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                ['Cliente',        detalle.cliente_nombre || '—'],
                ['N° Factura',     detalle.numero_factura_cliente || '—'],
                ['Proyecto',       detalle.numero_proyecto || '—'],
                ['Fecha Pago',     formatFecha(detalle.fecha_pago)],
                ['Monto',          `${simMon(detalle.moneda)}${formatMonto(detalle.monto)}`],
                ['Moneda',         detalle.moneda],
                ['Medio',          medioDisplay(detalle)],
                ['Banco destino',  detalle.banco_destino || '—'],
                ['N° Referencia',  detalle.numero_referencia || '—'],
                ['Estado Cobro',   detalle.cobro_estado || '—']
              ].map(([label, val]) => (
                <div key={label} style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '3px' }}>{label}</div>
                  <div style={{ fontSize: '13px', color: '#012E40', fontWeight: '500' }}>{val}</div>
                </div>
              ))}
              {detalle.comprobante_url && (
                <div style={{ gridColumn: '1/-1', background: '#eef5ff', border: '1px solid #c6d9f2', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '3px' }}>Comprobante</div>
                  <a href={detalle.comprobante_url} target='_blank' rel='noreferrer' style={{ fontSize: '13px', color: '#185FA5', textDecoration: 'underline' }}>
                    📎 Abrir comprobante
                  </a>
                </div>
              )}
              {detalle.notas && (
                <div style={{ gridColumn: '1/-1', background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '3px' }}>Notas</div>
                  <div style={{ fontSize: '13px', color: '#012E40' }}>{detalle.notas}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal pago */}
      {modalPago && (
        <ModalPagoCliente
          cobrosDisponibles={cobros}
          pagoEditar={pagoEditar}
          onGuardado={() => { setModalPago(false); setPagoEditar(null); cargar(); }}
          onCerrar={()   => { setModalPago(false); setPagoEditar(null); }}
        />
      )}

      {/* Modal eliminar */}
      {modalEliminar && (
        <ModalEliminar
          titulo='Eliminar pago recibido'
          descripcion={`${modalEliminar.numero_factura_cliente || '(sin #)'} — ${modalEliminar.cliente_nombre || ''}`}
          onConfirm={confirmarEliminar}
          onCancel={()  => setModalEliminar(null)}
        />
      )}
    </div>
  );
}
